type JobSiteAddressParts = {
  streetAddressPartOne?: string | null;
  streetAddressPartTwo?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
};

export const JOB_SITE_ADDRESS_REQUIRED_MESSAGES = {
  state: 'State is required',
} as const;

export type JobSiteAddressRequiredField =
  keyof typeof JOB_SITE_ADDRESS_REQUIRED_MESSAGES;

const normalize = (value: string | null | undefined) => value?.trim() ?? '';

/**
 * Job sites need a state. ZIP is optional; tax reporting uses the saved county
 * or falls back to a ZIP lookup when one is available.
 */
export function getMissingJobSiteAddressFields(
  address: JobSiteAddressParts | null | undefined,
): JobSiteAddressRequiredField[] {
  const missing: JobSiteAddressRequiredField[] = [];
  if (!normalize(address?.state)) missing.push('state');
  return missing;
}

/**
 * Compares the parts of an address that identify the location. County and
 * coordinates are left out: they are derived from these parts.
 */
export function hasJobSiteAddressLocationChanged(
  previous: JobSiteAddressParts | null | undefined,
  next: JobSiteAddressParts | null | undefined,
): boolean {
  return (
    [
      'streetAddressPartOne',
      'streetAddressPartTwo',
      'city',
      'state',
      'postalCode',
    ] as const
  ).some((field) => normalize(previous?.[field]) !== normalize(next?.[field]));
}

/**
 * Returns form errors for a job site address. Existing sites are only checked
 * when the address changes, so edits to other fields of legacy street-only
 * sites keep working.
 */
export function getJobSiteAddressErrors(
  address: JobSiteAddressParts | null | undefined,
  originalAddress?: JobSiteAddressParts | null,
): Partial<Record<JobSiteAddressRequiredField, string>> {
  if (
    originalAddress &&
    !hasJobSiteAddressLocationChanged(originalAddress, address)
  ) {
    return {};
  }
  return Object.fromEntries(
    getMissingJobSiteAddressFields(address).map((field) => [
      field,
      JOB_SITE_ADDRESS_REQUIRED_MESSAGES[field],
    ]),
  );
}

export function getJobSiteAddressErrorMessage(
  missing: JobSiteAddressRequiredField[],
): string {
  const labels = missing.map(() => 'state');
  return `Job site address needs a ${labels.join(' and ')}.`;
}
